import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements, PaymentElement, useStripe, useElements,
} from "@stripe/react-stripe-js";
import api from "../lib/api";
import { fmtEur, fmtDate } from "../lib/utils";
import { useI18n } from "../lib/i18n";
import {
  Lightning, Lock, CreditCard, ShieldCheck, CheckCircle, XCircle,
  Warning, Info, Trash, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

let _stripePromise = null;
function getStripe() {
  if (_stripePromise) return _stripePromise;
  _stripePromise = api.get("/stripe/config").then(({ data }) => loadStripe(data.publishable_key));
  return _stripePromise;
}

export default function ClientAutoRecharge({ embedded = false }) {
  const { t, lang } = useI18n();
  const [settings, setSettings] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [setupMode, setSetupMode] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentId, setSetupIntentId] = useState(null);
  const [threshold, setThreshold] = useState(30);
  const [amount, setAmount] = useState(200);
  const [consent, setConsent] = useState(false);

  const load = async () => {
    const { data } = await api.get("/client/auto-recharge");
    setSettings(data);
    setThreshold(data.threshold || 30);
    setAmount(data.amount || 200);
  };

  useEffect(() => { load(); setStripePromise(getStripe()); }, []);

  const startSetup = async () => {
    try {
      const { data } = await api.post("/client/auto-recharge/setup-intent");
      setClientSecret(data.client_secret);
      setSetupIntentId(data.setup_intent_id);
      setSetupMode(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ar_err_setup_creation"));
    }
  };

  const saveAmounts = async () => {
    try {
      await api.put("/client/auto-recharge", { threshold: Number(threshold), amount: Number(amount) });
      toast.success(t("ar_settings_saved"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
  };

  const disable = async () => {
    if (!window.confirm(t("ar_disable_confirm"))) return;
    await api.post("/client/auto-recharge/disable");
    toast.success(t("ar_disabled_toast"));
    load();
  };

  const removeCard = async () => {
    if (!window.confirm(t("ar_remove_card_confirm"))) return;
    await api.post("/client/auto-recharge/remove-card");
    toast.success(t("ar_card_removed"));
    load();
  };

  if (!settings) return <div className="text-[var(--tf-muted)]">{t("loading")}</div>;

  return (
    <div className={embedded ? "space-y-5 p-5" : "space-y-6 max-w-4xl"} data-testid="client-auto-recharge">
      {!embedded && (
        <div>
          <div className="tf-label">{t("wallet")}</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-1">{t("ar_title")}</h1>
          <p className="text-sm text-[var(--tf-muted)] mt-2 max-w-2xl">
            {t("ar_desc")}
          </p>
        </div>
      )}
      {embedded && (
        <p className="text-xs text-[var(--tf-muted)] -mt-1 max-w-2xl">
          {t("ar_desc")}
        </p>
      )}

      {/* Status banner */}
      <div className={`tf-card p-5 border-l-4 ${
        settings.enabled ? "border-[var(--tf-primary)] bg-[rgba(204,242,44,0.15)]"
                         : "border-[var(--tf-border)] bg-[var(--tf-bg)]"
      }`}>
        <div className="flex items-start gap-3">
          {settings.enabled
            ? <CheckCircle size={32} weight="fill" className="text-[var(--tf-ink)] shrink-0" />
            : <Lightning size={32} weight="duotone" className="text-[var(--tf-muted)] shrink-0" />
          }
          <div className="flex-1">
            <div className="font-display font-black text-lg">
              {settings.enabled ? t("ar_active") : t("ar_inactive")}
            </div>
            {settings.enabled ? (
              <div className="text-sm mt-1.5">
                {t("ar_active_desc", { threshold: fmtEur(settings.threshold), amount: fmtEur(settings.amount) })}
              </div>
            ) : (
              <div className="text-sm mt-1.5 text-[var(--tf-muted)]">
                {t("ar_inactive_desc")}
              </div>
            )}
            {settings.consecutive_failures > 0 && (
              <div className="text-xs text-[var(--tf-error)] mt-2 flex items-center gap-1.5">
                <Warning size={12} weight="bold" /> {t("ar_failed_attempts", { n: settings.consecutive_failures })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Setup or settings */}
      {setupMode ? (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance() }}>
          <SetupForm
            setupIntentId={setupIntentId}
            threshold={threshold}
            amount={amount}
            consent={consent}
            setThreshold={setThreshold}
            setAmount={setAmount}
            setConsent={setConsent}
            onSuccess={() => { setSetupMode(false); load(); }}
            onCancel={() => setSetupMode(false)}
            t={t}
          />
        </Elements>
      ) : settings.has_card ? (
        <ActiveCard
          settings={settings} threshold={threshold} amount={amount}
          setThreshold={setThreshold} setAmount={setAmount}
          onSave={saveAmounts} onDisable={disable} onRemoveCard={removeCard}
          onReEnable={async () => {
            await api.put("/client/auto-recharge", { threshold: Number(threshold), amount: Number(amount) });
            await api.post("/client/auto-recharge/disable"); // reset state
            toast.info(t("ar_reenable_note"));
          }}
          t={t}
          lang={lang}
        />
      ) : (
        <NoCardSetup onStart={startSetup} t={t} />
      )}

      {/* Recent attempts */}
      <RecentCharges charges={settings.recent_charges || []} lang={lang} t={t} />

      {/* Trust footer */}
      <div className="tf-card p-4 bg-[var(--tf-bg)]/40 flex items-center gap-3 text-xs text-[var(--tf-muted)]">
        <ShieldCheck size={20} weight="duotone" className="text-[var(--tf-ink)] shrink-0" />
        <div>
          {t("ar_trust_footer")}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Components ---------------- */

function NoCardSetup({ onStart, t }) {
  return (
    <div className="tf-card p-6 text-center" data-testid="no-card-setup">
      <CreditCard size={48} weight="duotone" className="mx-auto text-[var(--tf-muted)]" />
      <div className="font-display font-black text-xl mt-3">{t("ar_no_card_title")}</div>
      <p className="text-sm text-[var(--tf-muted)] mt-2 max-w-md mx-auto">
        {t("ar_no_card_desc")}
      </p>
      <button onClick={onStart} className="tf-btn tf-btn-primary mt-4" data-testid="start-setup-btn">
        <Lightning size={16} weight="bold" /> {t("ar_start_setup")}
      </button>
    </div>
  );
}

function ActiveCard({ settings, threshold, amount, setThreshold, setAmount, onSave, onDisable, onRemoveCard, t, lang }) {
  const brand = settings.card_brand || "card";
  const last4 = settings.card_last4 || "····";
  const exp = `${String(settings.card_exp_month).padStart(2, "0")}/${String(settings.card_exp_year).slice(-2)}`;
  return (
    <>
      <div className="grid md:grid-cols-2 gap-4">
        <NumberField label={t("ar_threshold_label")} sublabel={t("ar_threshold_sub")} value={threshold} setValue={setThreshold} min={5} max={1000} testid="thr-input" />
        <NumberField label={t("ar_amount_label")} sublabel={t("ar_amount_sub")} value={amount} setValue={setAmount} min={10} max={5000} testid="amt-input" />
      </div>

      <div className="tf-card p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-8 rounded bg-[var(--tf-ink)] text-white flex items-center justify-center text-[10px] font-display font-black uppercase">{brand}</div>
            <div>
              <div className="font-display font-bold text-sm">•••• •••• •••• {last4}</div>
              <div className="text-xs text-[var(--tf-muted)]">{t("ar_expiry", { exp, date: fmtDate(settings.consent_at, lang) })}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={onSave} className="tf-btn tf-btn-primary text-xs">{t("ar_save_settings")}</button>
            <button onClick={onDisable} className="tf-btn tf-btn-ghost text-xs">{settings.enabled ? t("ar_disable") : t("ar_already_disabled")}</button>
            <button onClick={onRemoveCard} className="tf-btn tf-btn-danger text-xs" title={t("common_remove")}><Trash size={14} weight="bold" /></button>
          </div>
        </div>
      </div>
    </>
  );
}

function SetupForm({ setupIntentId, threshold, amount, consent, setThreshold, setAmount, setConsent, onSuccess, onCancel, t }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!stripe || !elements) return;
    if (!consent) {
      setErr(t("ar_consent_required"));
      return;
    }
    setSubmitting(true);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.origin + "/client/auto-recharge" },
      redirect: "if_required",
    });
    if (error) {
      setErr(error.message || t("ar_err_auth"));
      setSubmitting(false);
      return;
    }
    if (setupIntent && setupIntent.status === "succeeded") {
      try {
        await api.post("/client/auto-recharge/confirm", {
          setup_intent_id: setupIntent.id,
          threshold: Number(threshold),
          amount: Number(amount),
          consent: true,
        });
        toast.success(t("ar_activated_toast"));
        onSuccess();
      } catch (e) {
        setErr(e?.response?.data?.detail || t("ar_err_save"));
        setSubmitting(false);
      }
    } else {
      setErr(t("ar_err_auth_incomplete", { status: setupIntent?.status }));
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <NumberField label={t("ar_threshold_label")} sublabel={t("ar_threshold_sub")} value={threshold} setValue={setThreshold} min={5} max={1000} testid="thr-input-setup" />
        <NumberField label={t("ar_amount_label")} sublabel={t("ar_amount_sub_vat")} value={amount} setValue={setAmount} min={10} max={5000} testid="amt-input-setup" />
      </div>

      <div className="tf-card p-5">
        <div className="tf-label flex items-center gap-2 mb-3"><Lock size={12} weight="bold" /> {t("ar_card_setup_title")}</div>
        <PaymentElement options={{ layout: "tabs" }} />
      </div>

      <label className="flex items-start gap-3 cursor-pointer p-4 tf-card hover:bg-[var(--tf-bg)]/50 transition-colors">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 w-4 h-4 accent-[var(--tf-primary)]"
          data-testid="consent-checkbox"
        />
        <div className="text-sm leading-relaxed">
          <b>{t("ar_consent_note", { amount: fmtEur(amount), threshold: fmtEur(threshold) })}</b>
          <div className="text-xs text-[var(--tf-muted)] mt-2">
            {t("ar_consent_sub")}
          </div>
        </div>
      </label>

      {err && (
        <div className="tf-card p-3 border-l-4 border-[var(--tf-error)] text-sm text-[var(--tf-error)] flex items-start gap-2">
          <XCircle size={16} weight="fill" className="mt-0.5 shrink-0" /> {err}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button type="submit" disabled={!stripe || submitting} className="tf-btn tf-btn-primary" data-testid="confirm-setup-btn">
          {submitting ? t("ar_verifying") : t("ar_activate_btn")}
        </button>
        <button type="button" onClick={onCancel} className="tf-btn tf-btn-ghost">{t("ar_cancel")}</button>
      </div>
    </form>
  );
}

function NumberField({ label, sublabel, value, setValue, min, max, testid }) {
  return (
    <div className="tf-card p-4">
      <div className="tf-label">{label}</div>
      <div className="text-xs text-[var(--tf-muted)] mt-0.5">{sublabel}</div>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-[var(--tf-muted)] font-display font-bold text-lg">€</span>
        <input
          type="number" min={min} max={max} step="1"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="tf-input tf-numeric text-2xl font-bold !py-2"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function RecentCharges({ charges, lang, t }) {
  if (!charges || charges.length === 0) return null;
  return (
    <div className="tf-card overflow-hidden">
      <div className="p-4 border-b border-[var(--tf-border)] flex items-center gap-2 font-display font-bold">
        <ArrowsClockwise size={16} weight="bold" /> {t("ar_recent_title")}
      </div>
      <table className="tf-table">
        <thead><tr><th>{t("ar_col_date")}</th><th className="text-right">{t("ar_col_amount")}</th><th>{t("ar_col_status")}</th><th>{t("ar_col_reason")}</th></tr></thead>
        <tbody>
          {charges.map((c) => (
            <tr key={c.id} data-testid={`charge-${c.id}`}>
              <td className="text-xs font-mono">{fmtDate(c.created_at_iso, lang)}</td>
              <td className="text-right tf-numeric font-bold">{fmtEur(c.amount)}</td>
              <td>
                <span className={`tf-status-pill ${
                  c.status === "credited" ? "tf-status-success"
                  : c.status === "failed" ? "tf-status-error"
                  : "tf-status-warning"
                }`}>
                  {c.status === "credited" ? t("ar_status_success")
                    : c.status === "failed" ? t("ar_status_failed")
                    : c.status}
                </span>
                {c.status === "failed" && c.error_message && (
                  <div className="text-[10px] text-[var(--tf-error)] mt-1">{c.error_message}</div>
                )}
              </td>
              <td className="text-xs text-[var(--tf-muted)]">{c.reason || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function stripeAppearance() {
  return {
    theme: "stripe",
    variables: {
      colorPrimary: "#1f3825",
      colorBackground: "#FFFFFF",
      colorText: "#1f3825",
      fontFamily: "Nohemi, system-ui, sans-serif",
      borderRadius: "8px",
      spacingUnit: "4px",
    },
  };
}
